import { makeT } from "app/client/lib/localization";
import { sanitizeLinkUrl } from "app/client/lib/sanitizeUrl";
import * as oauthCss from "app/client/ui/OAuthCss";
import { createUserImage } from "app/client/ui/UserImage";
import { cssMemberPrimary } from "app/client/ui/UserItem";
import { testId, theme } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { cssLink } from "app/client/ui2018/links";
import { confirmModal } from "app/client/ui2018/modals";
import { IGristUrlState } from "app/common/gristUrls";
import { tryParseUrl } from "app/common/gutil";
import { OAuthPageClient } from "app/common/OAuthClientTypes";
import { tokens } from "app/common/ThemePrefs";

import { dom, DomArg, DomContents, styled } from "grainjs";

const t = makeT("OAuthUIComponents");

type IGristUrlOAuthParams = { oauthClientId: string } | { oauthGrantId: string };

// Helper for UrlState to encode oauth params for per-client and per-grant pages. These are
// unknown to IGristUrlState, but it encodes all params right into the URL, so they just work.
export function getUrlStateForOAuth(params: IGristUrlOAuthParams): IGristUrlState {
  const account = "oauthClientId" in params ? "developer" : "authorized-apps";
  return { account, params: params as IGristUrlState["params"] };
}

// Helper to decode params for per-client and per-grant pages. These are unknown to IGristUrlState
// so we just extract them directly from the current page's URL.
export function getParamFromUrl(param: "oauthClientId" | "oauthGrantId"): string | null {
  return new URLSearchParams(window.location.search).get(param);
}

/**
 * Opens the "Grist server info" modal describing the OAuth client.
 *
 * Shared between the SaaS login page's client-info tooltip (for sign-in-with-grist)
 * and the OAuth Apps consent page (`ext/app/client/ui/OAuthFlow.ts`).
 */
export function openClientInfoModal(client: OAuthPageClient, titleOverride?: DomContents): void {
  // The "URL" we trust is the origin of the client's registered redirect URI. The app's own
  // `client.uri` (homepage) is developer-supplied; we only show it if it differs from the trusted
  // redirect origin, otherwise it's redundant.
  const redirectOrigin = getRedirectOrigin(client);
  const homepage = client.uri && client.uri !== redirectOrigin ? client.uri : "";
  const hasUntrusted = Boolean(homepage || client.description || client.supportContact);
  confirmModal(titleOverride ?? t("About this app"), "Got it", () => {}, {
    explanation: dom("div",
      dom("p", cssLabel("Name: "), dom("span", client.name, testId("oauth-client-info-name"))),
      redirectOrigin ? dom("p",
        cssLabel("URL: "),
        cssLinkBreakAll(redirectOrigin, {
          href: sanitizeLinkUrl(redirectOrigin),
          target: "_blank",
        }, testId("oauth-client-info-uri")),
      ) : null,
      client.registeredBy ? dom("p",
        cssLabel("Registered by: "),
        cssLinkBreakAll(client.registeredBy, {
          href: sanitizeLinkUrl(`mailto:${client.registeredBy}`),
        }, testId("oauth-client-info-registered-by")),
      ) : null,
      hasUntrusted ? cssUntrustedHeader("Provided by the app · not verified by Grist") : null,
      homepage ? dom("p",
        cssLabel("Homepage: "),
        cssLinkBreakAll(homepage, {
          href: sanitizeLinkUrl(homepage),
          target: "_blank",
        }, testId("oauth-client-info-homepage")),
      ) : null,
      client.description ? dom("p",
        cssLabel("Description: "),
        cssPreWrap(client.description, testId("oauth-client-info-description")),
      ) : null,
      client.supportContact ? dom("p",
        cssLabel("Support contact: "),
        cssLinkBreakAll(client.supportContact, {
          href: sanitizeLinkUrl(`mailto:${client.supportContact}`),
        }, testId("oauth-client-info-support-contact")),
      ) : null,
    ),
    hideCancel: true,
  });
}

/**
 * Origin (scheme + host + port) of the client's first registered redirect URI. Surface in
 * the info modal / grey strip as "the URL Grist will send your browser to next." Returns
 * `""` if the client has no redirect URI or it can't be parsed.
 */
function getRedirectOrigin(client: OAuthPageClient): string {
  return tryParseUrl(client.redirectUris[0])?.origin ?? "";
}

/**
 * Renders the "Authorizing as" pill — user avatar, email, pencil icon — used on the
 * OAuth select-account trigger. Shared between the SaaS login consent page and the
 * OAuth Apps consent page.
 */
export function buildAuthorizingAsButton(
  user: { name?: string; email: string },
  ...buttonArgs: DomArg<HTMLElement>[]
): HTMLButtonElement {
  return oauthCss.changeAccountButton(
    oauthCss.accountSmall(
      createUserImage(user, "small"),
      oauthCss.accountNameAndEmail(cssMemberPrimary(
        dom("span", user.email, testId("oauth-consent-change-account-email"))),
      ),
      icon("Pencil"),
    ),
    ...buttonArgs,
  );
}

/**
 * Compact "about the app" strip: bold app name, redirect origin, "See info" link
 * (opens the client-info modal), plus an optional muted description below. Identifies
 * the OAuth client at the top of both the consent page and the Manage Grant dialog.
 */
export function buildAboutStrip(client: OAuthPageClient, ...domArgs: DomArg[]): DomContents {
  const origin = getRedirectOrigin(client);
  return cssSection(
    cssSectionLabel("About this app"),
    cssAboutStrip(
      cssAboutHeadline(
        cssAboutAppIcon("Widget"),
        cssAboutName(client.name, testId("oauth-about-name")),
        origin ? cssAboutSep(" - ") : null,
        origin ? dom("span", origin, testId("oauth-about-origin")) : null,
        cssAboutSep(" - "),
        cssAboutInfoLink(
          { href: "#" },
          dom.on("click", (e: Event) => { e.preventDefault(); openClientInfoModal(client); }),
          icon("Question"),
          "See info",
          testId("oauth-about-info"),
        ),
      ),
      client.description ? cssAboutDescription(client.description, testId("oauth-about-description")) : null,
    ),
    ...domArgs,
  );
}

export const cssSection = styled("section", `
  display: flex;
  flex-direction: column;
  gap: 8px;
`);

export const cssSectionLabel = styled("div", `
  color: ${theme.lightText};
  font-size: ${tokens.mediumFontSize};
  font-weight: bold;
`);

const cssAboutStrip = styled("div", `
  padding: 16px;
  background: ${tokens.bgSecondary};
  border-radius: ${tokens.controlBorderRadius};
  color: ${theme.text};
  font-size: ${tokens.mediumFontSize};
  display: flex;
  flex-direction: column;
  gap: 8px;
`);

const cssAboutHeadline = styled("div", `
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  overflow-wrap: anywhere;
`);

const cssAboutName = styled("span", `
  font-weight: 700;
`);

const cssAboutSep = styled("span", `
  color: ${tokens.secondary};
`);

const cssAboutDescription = styled("div", `
  margin-left: 32px;
  color: ${tokens.secondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`);

const cssAboutInfoLink = styled(cssLink, `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  --icon-color: currentColor;
`);

const cssAboutAppIcon = styled(icon, `
  --icon-color: ${tokens.secondary};
  margin-right: 8px;
`);

const cssLinkBreakAll = styled(cssLink, `
  word-break: break-all;
`);

const cssLabel = styled("span", `
  font-weight: 600;
`);

const cssPreWrap = styled("span", `
  white-space: pre-wrap;
`);

const cssUntrustedHeader = styled("p", `
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${tokens.decoration};
  font-style: italic;
  color: ${tokens.secondary};
`);
